import {
  DeleteService,
  GetService,
  GetServices,
  GetServiceMarketplaceCategories,
  PostService,
  PostServiceSortOrder,
  GetServiceAddons,
  PostServiceAddon,
  PostServiceAddonSortOrder,
  GetServiceAddon,
  DeleteServiceAddon,
  PutServiceAddon,
  GetServiceAddonCategories,
  PostServiceAddonCategory,
  GetServiceAddonCategory,
  DeleteServiceAddonCategory,
  PutServiceAddonCategory,
  GetServiceImages,
  GetServiceImageUploadUrl,
  PostCompleteServiceImageUpload,
  DeleteServiceImage,
  GetServiceAddonImages,
  GetServiceAddonImageUploadUrl,
  PostCompleteServiceAddonImageUpload,
  DeleteServiceAddonImage,
  PutServiceImagesOrder,
  PutServiceAddonImagesOrder,
  GetServicePrice,
  PostServicePrice,
  PutServicePrice,
} from "@/server-types";
import { del, get, post, put } from "../request-helpers";
import { useCustomQuery } from "../custom-query";
import { useCustomMutation } from "../custom-mutation";
import { queryClient } from "..";

/**
 * GET /service
 */
export const useGetServices = (
  query: GetServices["query"] & {
    enabled?: boolean;
  },
) => {
  return useCustomQuery({
    queryKey: ["services", query],
    queryFn: () => get<GetServices>("/service", query),
    enabled: query.enabled,
  });
};

/**
 * GET /service/marketplace-category
 */
export const useGetServiceMarketplaceCategories = () => {
  return useCustomQuery({
    queryKey: ["service-marketplace-categories"],
    queryFn: () =>
      get<GetServiceMarketplaceCategories>("/service/marketplace-category"),
  });
};

/**
 * POST /service
 */
export const usePostService = () => {
  return useCustomMutation({
    mutationKey: ["services"],
    mutationFn: (service: PostService["body"]) =>
      post<PostService>("/service", service),
  });
};

export const useGetServiceAddons = ({
  query,
  disabled,
}: {
  query?: GetServiceAddons["body"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["service-addons", query],
    queryFn: () => post<GetServiceAddons>("/service/addon/search", query ?? {}),
    enabled: !disabled,
  });
};

export const usePostServiceAddon = () => {
  return useCustomMutation({
    mutationKey: ["service-addons"],
    mutationFn: (addon: PostServiceAddon["body"]) =>
      post<PostServiceAddon>("/service/addon", addon),
  });
};

export const usePostServiceAddonSortOrder = () => {
  return useCustomMutation({
    mutationKey: ["postServiceAddonsOrder"],
    mutationFn: (addonIds: number[]) =>
      post<PostServiceAddonSortOrder>(`/service/addon/sort-order`, {
        addonIds,
      }),
  });
};

/**
 * GET /service/:serviceId/price
 *
 * A service's price history: what it costs now and what it cost on the Croatian
 * reference date. `anchor` is null when nothing is on record for that date —
 * render that as "not known", never as the current price.
 */
export const useGetServicePrice = (serviceId: number | undefined) => {
  return useCustomQuery({
    queryKey: ["service-price", serviceId],
    queryFn: () => get<GetServicePrice>(`/service/${serviceId!}/price`),
    enabled: serviceId != null && serviceId !== -1,
  });
};

// A service's price rows are its anchors too, so the anchor review and its banner change with them.
function invalidateServicePrice(serviceId: number) {
  queryClient.invalidateQueries({ queryKey: ["service-price", serviceId] });
  queryClient.invalidateQueries({ queryKey: ["anchor-review"] });
}

/**
 * POST /service/:serviceId/price — state a price effective from a date.
 *
 * `effectiveFrom` is a `YYYY-MM-DD` string on the wire. The endpoint's body
 * type is what the zod schema produces *after* parsing, which is a `Date`, so
 * the sent shape is spelled out here rather than reused.
 */
export type ServicePriceRequestBody = Omit<
  PostServicePrice["body"],
  "effectiveFrom"
> & { effectiveFrom: string };

export const usePostServicePrice = (
  serviceId: number,
  options?: { onSuccess?: () => void },
) => {
  return useCustomMutation({
    mutationKey: ["service-price", serviceId],
    mutationFn: (body: ServicePriceRequestBody) =>
      post<PostServicePrice>(`/service/${serviceId}/price`, body),
    onSuccess: () => {
      invalidateServicePrice(serviceId);
      options?.onSuccess?.();
    },
  });
};

/**
 * PUT /service/:serviceId/price/:effectiveFrom — correct a price that exists.
 */
export const usePutServicePrice = (
  serviceId: number,
  options?: { onSuccess?: () => void },
) => {
  return useCustomMutation({
    mutationKey: ["service-price", serviceId],
    mutationFn: ({
      effectiveFrom,
      ...body
    }: PutServicePrice["body"] & { effectiveFrom: string }) =>
      put<PutServicePrice>(
        `/service/${serviceId}/price/${effectiveFrom}`,
        body,
      ),
    onSuccess: () => {
      invalidateServicePrice(serviceId);
      options?.onSuccess?.();
    },
  });
};

/**
 * GET /service/:id
 */
export const useGetService = (id: number | undefined) => {
  return useCustomQuery({
    queryKey: ["service", id],
    queryFn: () => get<GetService>(`/service/${id!}`),
    enabled: id !== -1 && id != null,
  });
};

/**
 * DELETE /service/:id
 */
export const useDeleteService = () => {
  return useCustomMutation({
    mutationKey: ["services"],
    mutationFn: (id: number) => del<DeleteService>(`/service/${id}`),
  });
};

/**
 * POST /service/sort-order
 */

export const usePostServiceSortOrder = () => {
  return useCustomMutation({
    mutationKey: ["postServicesOrder"],
    mutationFn: (services: number[]) =>
      post<PostServiceSortOrder>(`/service/sort-order`, { services }),
  });
};

export const useGetServiceAddon = (id: number) => {
  return useCustomQuery({
    queryKey: ["service-addon", id],
    queryFn: () => get<GetServiceAddon>(`/service/addon/${id}`),
    enabled: id !== -1,
  });
};

export const useDeleteServiceAddon = () => {
  return useCustomMutation({
    mutationKey: ["service-addons"],
    mutationFn: (id: number) => del<DeleteServiceAddon>(`/service/addon/${id}`),
  });
};

export const usePutServiceAddon = () => {
  return useCustomMutation({
    mutationKey: ["service-addons"],
    mutationFn: (addon: PutServiceAddon["body"] & { id: number }) =>
      put<PutServiceAddon>(`/service/addon/${addon.id}`, addon),
  });
};

export const useGetServiceAddonCategories = (
  query?: GetServiceAddonCategories["query"],
) => {
  return useCustomQuery({
    queryKey: ["service-addon-categories", query],
    queryFn: () =>
      get<GetServiceAddonCategories>("/service/addon/category", query ?? {}),
  });
};

export const usePostServiceAddonCategory = () => {
  return useCustomMutation({
    mutationKey: ["service-addon-categories"],
    mutationFn: (category: PostServiceAddonCategory["body"]) =>
      post<PostServiceAddonCategory>("/service/addon/category", category),
  });
};

export const useGetServiceAddonCategory = (id: number) => {
  return useCustomQuery({
    queryKey: ["service-addon-category", id],
    queryFn: () =>
      get<GetServiceAddonCategory>(`/service/addon/category/${id}`),
    enabled: id !== -1,
  });
};

export const useDeleteServiceAddonCategory = () => {
  return useCustomMutation({
    mutationKey: ["service-addon-categories"],
    mutationFn: (id: number) =>
      del<DeleteServiceAddonCategory>(`/service/addon/category/${id}`),
  });
};

export const usePutServiceAddonCategory = () => {
  return useCustomMutation({
    mutationKey: ["service-addon-categories"],
    mutationFn: (category: PutServiceAddonCategory["body"] & { id: number }) =>
      put<PutServiceAddonCategory>(
        `/service/addon/category/${category.id}`,
        category,
      ),
  });
};

/**
 * GET /service/:serviceId/images
 */
export const useGetServiceImages = (id: number) => {
  return useCustomQuery({
    queryKey: ["serviceImages", id],
    queryFn: () => get<GetServiceImages>(`/service/${id}/images`),
    enabled: id > 0,
  });
};

/**
 * GET /service/:serviceId/images/upload
 */
export const useGetServiceImageUploadUrl = () => {
  return useCustomMutation({
    mutationKey: ["getServiceImageUploadUrl"],
    mutationFn: ({
      serviceId,
      query,
    }: {
      serviceId: number;
      query: GetServiceImageUploadUrl["query"];
    }) =>
      get<GetServiceImageUploadUrl>(
        `/service/${serviceId}/images/upload`,
        query,
      ),
  });
};

/**
 * POST /service/:serviceId/images/upload/complete
 */
export const useCompleteServiceImageUpload = () => {
  return useCustomMutation({
    mutationKey: ["completeServiceImageUpload"],
    mutationFn: ({
      serviceId,
      body,
    }: {
      serviceId: number;
      body: PostCompleteServiceImageUpload["body"];
    }) =>
      post<PostCompleteServiceImageUpload>(
        `/service/${serviceId}/images/upload/complete`,
        body,
      ),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["serviceImages", variables.serviceId],
      });
    },
  });
};

/**
 * DELETE /service/:serviceId/images/:imageId
 */
export const useDeleteServiceImage = () => {
  return useCustomMutation({
    mutationFn: ({
      serviceId,
      imageId,
    }: {
      serviceId: number;
      imageId: number;
    }) => del<DeleteServiceImage>(`/service/${serviceId}/images/${imageId}`),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["serviceImages", variables.serviceId],
      });
    },
  });
};

/**
 * PUT /service/:serviceId/images/order
 */
export const usePutServiceImagesOrder = () => {
  return useCustomMutation({
    mutationFn: ({
      serviceId,
      body,
    }: {
      serviceId: number;
      body: PutServiceImagesOrder["body"];
    }) =>
      put<PutServiceImagesOrder>(`/service/${serviceId}/images/order`, body),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["serviceImages", variables.serviceId],
      });
    },
  });
};

/**
 * GET /service/addon/:serviceAddonId/images
 */
export const useGetServiceAddonImages = (id: number) => {
  return useCustomQuery({
    queryKey: ["serviceAddonImages", id],
    queryFn: () => get<GetServiceAddonImages>(`/service/addon/${id}/images`),
    enabled: id > 0,
  });
};

/**
 * GET /service/addon/:serviceAddonId/images/upload
 */
export const useGetServiceAddonImageUploadUrl = () => {
  return useCustomMutation({
    mutationKey: ["getServiceAddonImageUploadUrl"],
    mutationFn: ({
      serviceAddonId,
      query,
    }: {
      serviceAddonId: number;
      query: GetServiceAddonImageUploadUrl["query"];
    }) =>
      get<GetServiceAddonImageUploadUrl>(
        `/service/addon/${serviceAddonId}/images/upload`,
        query,
      ),
  });
};

/**
 * POST /service/addon/:serviceAddonId/images/upload/complete
 */
export const useCompleteServiceAddonImageUpload = () => {
  return useCustomMutation({
    mutationKey: ["completeServiceAddonImageUpload"],
    mutationFn: ({
      serviceAddonId,
      body,
    }: {
      serviceAddonId: number;
      body: PostCompleteServiceAddonImageUpload["body"];
    }) =>
      post<PostCompleteServiceAddonImageUpload>(
        `/service/addon/${serviceAddonId}/images/upload/complete`,
        body,
      ),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["serviceAddonImages", variables.serviceAddonId],
      });
    },
  });
};

/**
 * PUT /service/addon/:serviceAddonId/images/order
 */
export const usePutServiceAddonImagesOrder = () => {
  return useCustomMutation({
    mutationFn: ({
      serviceAddonId,
      body,
    }: {
      serviceAddonId: number;
      body: PutServiceAddonImagesOrder["body"];
    }) =>
      put<PutServiceAddonImagesOrder>(
        `/service/addon/${serviceAddonId}/images/order`,
        body,
      ),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["serviceAddonImages", variables.serviceAddonId],
      });
    },
  });
};

/**
 * DELETE /service/addon/:serviceAddonId/images/:imageId
 */
export const useDeleteServiceAddonImage = () => {
  return useCustomMutation({
    mutationFn: ({
      serviceAddonId,
      imageId,
    }: {
      serviceAddonId: number;
      imageId: number;
    }) =>
      del<DeleteServiceAddonImage>(
        `/service/addon/${serviceAddonId}/images/${imageId}`,
      ),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["serviceAddonImages", variables.serviceAddonId],
      });
    },
  });
};
